import type { PointerEvent as ReactPointerEvent } from 'react';

const selectNodeContents = (section: HTMLElement): void => {
  const { ownerDocument } = section;
  const selection = ownerDocument.getSelection();
  if (!selection) {
    return;
  }
  const range = ownerDocument.createRange();
  range.selectNodeContents(section);
  selection.removeAllRanges();
  selection.addRange(range);
};

const resolveSectionFromEvent = (
  event: ReactPointerEvent | PointerEvent,
  root: HTMLElement | null,
): HTMLElement | null => {
  if (!root) {
    return null;
  }
  if ('button' in event && event.button !== 0) {
    return null;
  }

  const eventTarget = event.target;
  if (!(eventTarget instanceof Element) || !root.contains(eventTarget)) {
    return null;
  }

  const section = eventTarget.closest('[role="spinbutton"]');
  if (!(section instanceof HTMLElement) || !root.contains(section)) {
    return null;
  }

  return section;
};

/**
 * Prevent the browser from placing a caret mid-section on click, then select the whole
 * spinbutton. MUI already tries to select the section, but contenteditable + click often
 * leaves a partial digit/letter selection afterwards.
 */
export const claimEntireFieldSectionOnPointerDown = (
  event: ReactPointerEvent | PointerEvent,
  root: HTMLElement | null,
): number | null => {
  const section = resolveSectionFromEvent(event, root);
  if (!section || !root) {
    return null;
  }

  // Stop the browser caret/partial selection that would otherwise win over MUI's sync.
  event.preventDefault();

  const sections = Array.from(root.querySelectorAll('[role="spinbutton"]'));
  const sectionIndex = sections.indexOf(section);
  if (sectionIndex < 0) {
    return null;
  }

  section.focus({ preventScroll: true });
  selectNodeContents(section);

  // Re-assert after MUI's own focus/selection handlers run.
  queueMicrotask(() => selectNodeContents(section));
  requestAnimationFrame(() => selectNodeContents(section));

  return sectionIndex;
};

/**
 * Safety net if anything re-applied a caret between pointerdown and pointerup.
 */
export const selectEntireFieldSectionOnPointerUp = (
  event: ReactPointerEvent | PointerEvent,
  root: HTMLElement | null,
): void => {
  const section = resolveSectionFromEvent(event, root);
  if (!section) {
    return;
  }
  selectNodeContents(section);
};
